import * as client from "../../../client";
import { SafeReportResponse } from "../Conversion";
import { formatExactMoneyDollars } from "../Money";
import {
  isLossRatioPeril,
  PERIL_TYPE_ORDER,
  readableLayer,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
} from "../layers/util";
import {
  DBPerilType,
  perilSentenceFromEnabledPerils,
  readableNameFromPeril,
} from "../general-input/util";
import {
  claimsFromLossRunInfo,
  Loss,
  lossRunInfoToLossRunYears,
  lossRunStatsForWindow,
  parsePolicyPeriod,
} from "../loss-run/util";
import {
  buildRiskDescription,
  enabledAddressesFromReport,
} from "./riskDescription";
import { blendedItvPerSqft } from "../sov/utils";
import { decodeMentions } from "../../../components/comments/mentions";

// Builds the editable "Post Bind snapshot" that seeds the Referral tab: an
// inline-styled two-column table (label / value) covering the same sections as
// the read-only snapshot. Inline styles are used because TinyMCE strips CSS
// classes — they survive copy-to-email and the PDF export. A section is omitted
// when its value can't be derived; everything is editable once seeded.

const money = (dollars: number): string =>
  formatExactMoneyDollars(dollars, {
    withRounding: true,
    withDollarSign: true,
  });

const formatRolPercent = (fraction: number): string => {
  const pct = fraction * 100;
  const rounded =
    Math.abs(pct - Math.round(pct)) < 0.05
      ? Math.round(pct).toString()
      : pct.toFixed(1);
  return `${rounded}%`;
};

const esc = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const COVERAGE_FIELDS: { key: keyof client.RawAddress; label: string }[] = [
  { key: "building_value", label: "Buildings" },
  { key: "bpp_value", label: "Business Personal Property" },
  { key: "bi_incl_ee_value", label: "Business Income including Extra Expense" },
  { key: "bi_excl_ee_value", label: "Business Income" },
  { key: "contractors_equipment_value", label: "Contractors Equipment" },
  { key: "inventory_value", label: "Inventory" },
  { key: "tenant_betterments_value", label: "Tenant Betterments" },
  { key: "furniture_and_fixtures_value", label: "Furniture & Fixtures" },
  { key: "signs_value", label: "Signs" },
  {
    key: "outdoor_property_and_autos_value",
    label: "Outdoor Property & Autos",
  },
];

const hasPositiveValue = (raw: string | null | undefined): boolean => {
  if (!raw) return false;
  const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) && n > 0;
};

const coverageList = (addresses: client.RawAddress[]): string | null => {
  const present = COVERAGE_FIELDS.filter(({ key }) =>
    addresses.some((a) => hasPositiveValue(a[key] as string | null | undefined))
  ).map(({ label }) => label);
  const deduped = present.includes("Business Income including Extra Expense")
    ? present.filter((l) => l !== "Business Income")
    : present;
  return deduped.length > 0 ? deduped.join(", ") : null;
};

const lossYear = (loss: Loss): string | null => {
  const fromDate = loss.date_of_loss?.match(/\d{4}/)?.[0];
  if (fromDate) return fromDate;
  const period = parsePolicyPeriod(loss.policy_year);
  return period ? String(period[0]) : null;
};

// Returns safe HTML (the bold basis label survives the snapshot/email/PDF),
// so the caller must NOT re-escape it — the only free-text field (the loss
// description) is escaped here.
const lossHistorySummary = (lossRunInfo: client.LossRunInfo): string => {
  const stats = lossRunStatsForWindow(
    lossRunInfoToLossRunYears(lossRunInfo),
    5
  );

  // Carrier loss runs frequently report only one of the two loss bases. Prefer
  // net incurred when present, else fall back to ground-up, and label whichever
  // basis the figures represent — rather than reporting "No reported losses"
  // when only one basis is populated.
  const netAvailable =
    stats.totalNetIncurred != null && stats.totalNetIncurred > 0;
  const basis = netAvailable
    ? {
        label: "Net incurred",
        field: "net_incurred" as const,
        total: stats.totalNetIncurred,
        avg: stats.avgNetIncurred,
      }
    : {
        label: "Ground up",
        field: "ground_up_loss" as const,
        total: stats.totalGroundUpLoss,
        avg: stats.avgGroundUpLoss,
      };

  const parts: string[] = [];
  if (basis.total != null && basis.total > 0) {
    parts.push(`${money(basis.total)} in past ${stats.windowYears} years`);
  }
  if (basis.avg != null && basis.avg > 0) {
    parts.push(`${money(basis.avg)} average per year`);
  }

  const claims = claimsFromLossRunInfo(lossRunInfo);
  const largest = claims.reduce<Loss | null>(
    (max, c) => ((c[basis.field] ?? 0) > (max?.[basis.field] ?? 0) ? c : max),
    null
  );
  const largestAmount = largest?.[basis.field] ?? 0;
  if (largest != null && largestAmount > 0) {
    const year = lossYear(largest);
    const cause = largest.description ? ` (${esc(largest.description)})` : "";
    parts.push(
      `largest loss of ${money(largestAmount)}${
        year ? ` in ${year}` : ""
      }${cause}`
    );
  }

  if (parts.length === 0) return "No reported losses";
  return `<b>${basis.label}:</b> ${parts.join("; ")}`;
};

// Per-policy-year underwriter comments, newest year first. Mentions are decoded
// to readable "@Name" text and all free text is escaped. Returns null when there
// are no non-empty comments, so the row is omitted.
const lossRunCommentsSummary = (
  lossRunComments: client.Comment[]
): string | null => {
  const byYear = new Map<string, string[]>();
  for (const comment of lossRunComments) {
    const year = comment.target_key;
    if (!year) continue;
    const text = decodeMentions(comment.message).trim();
    if (!text) continue;
    const existing = byYear.get(year);
    if (existing) existing.push(text);
    else byYear.set(year, [text]);
  }
  const lines = [...byYear.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(
      ([year, texts]) => `<b>${esc(year)}:</b> ${texts.map(esc).join("; ")}`
    );
  return lines.length > 0 ? lines.join("<br>") : null;
};

// Per-peril policy-level deductibles: prefer manual, else first extracted file.
const deductibleEntries = (
  info: client.AdvancedDeductibleInfo | null | undefined
): { label: string; value: string }[] => {
  if (!info) return [];
  const descFor = (peril: string): string | undefined => {
    const manual = info.manual_deductibles[peril]?.short_deductible_description;
    if (manual) return manual;
    for (const file of Object.values(info.extracted_deductibles)) {
      const d = file[peril]?.short_deductible_description;
      if (d) return d;
    }
    return undefined;
  };
  return PERIL_TYPE_ORDER.flatMap((peril) => {
    const value = descFor(peril);
    return value ? [{ label: readableNameFromPeril(peril), value }] : [];
  });
};

// Only the CAT perils an underwriter checks on a referral get a limit row; the
// rest are covered by the Limit of Liability row.
const REFERRAL_LIMIT_PERILS: DBPerilType[] = [
  "earthquake",
  "earth_movement",
  "flood",
];

// Per-peril policy-level limits: prefer manual, else first extracted file. A
// peril is skipped unless the policy actually includes it.
const limitEntries = (
  info: client.AdvancedLimitInfo | null | undefined,
  includedPerils: DBPerilType[]
): { label: string; value: string }[] => {
  if (!info) return [];
  const descFor = (peril: string): string | undefined => {
    const manual = info.manual_limits[peril]?.short_limit_description;
    if (manual) return manual;
    for (const file of Object.values(info.extracted_limits)) {
      const d = file[peril]?.short_limit_description;
      if (d) return d;
    }
    return undefined;
  };
  return PERIL_TYPE_ORDER.filter(
    (peril) =>
      REFERRAL_LIMIT_PERILS.includes(peril) && includedPerils.includes(peril)
  ).flatMap((peril) => {
    const value = descFor(peril);
    return value ? [{ label: readableNameFromPeril(peril), value }] : [];
  });
};

const pricingString = (
  layer: client.Layer,
  breakdown: client.LayerPremiumBreakdown | undefined
): string | null => {
  if (!breakdown) return null;
  const totals = breakdown.totals["resolved"];
  if (!totals) return null;

  const head = `${money(totals.layer_total)} layer / ${money(
    totals.share_total
  )} share`;

  const tail: string[] = [];
  if (layer.range > 0) {
    tail.push(`${formatRolPercent(totals.layer_total / layer.range)} ROL`);
  }
  const sumAal = Object.values(breakdown.peril_types).reduce(
    (acc, pt) =>
      isLossRatioPeril(pt.resolved.peril_type)
        ? acc + (pt.resolved.total_aal ?? 0)
        : acc,
    0
  );
  if (sumAal > 0) {
    tail.push(`${(totals.layer_total / sumAal).toFixed(2)}xAAL`);
  }

  return tail.length > 0 ? `${head}; ${tail.join("; ")}` : head;
};

const LABEL_STYLE =
  "background-color:#c7d6ef;border:1px solid #a9bfe0;padding:6px 10px;font-weight:bold;width:210px;vertical-align:top;color:#1f2d4d;";
const VALUE_STYLE =
  "border:1px solid #a9bfe0;padding:6px 10px;vertical-align:top;";

export const buildSnapshotReferralHtml = (
  specialtyPropertyInfo: SafeReportResponse,
  premiumData: client.CalculatedPremiumResponse | undefined,
  lossRunComments: client.Comment[]
): string => {
  const report = specialtyPropertyInfo.report_json;
  const policy = report.policy_info;
  const ci = report.company_info;

  const addresses = enabledAddressesFromReport(specialtyPropertyInfo);
  const totalTiv = addresses.reduce((acc, a) => acc + (a.tiv ?? 0), 0);

  const sortedLayers = sortedLayersFromReport(specialtyPropertyInfo);
  const enabledLayers = sortedLayers.filter((l) => l.is_enabled);
  const layers =
    enabledLayers.length > 0 ? enabledLayers : sortedLayers.slice(0, 1);
  const multipleLayers = layers.length > 1;

  const effectiveDates = ci.expiration_date
    ? `${ci.effective_date} – ${ci.expiration_date}`
    : ci.effective_date;

  const limitHtml = layers
    .map((l) => esc(readableLimitOfLiabilityFromLayer(l)))
    .join("<br>");

  const premiumHtml = layers
    .flatMap((l) => {
      const breakdown = premiumData?.layers.find(
        (b) => b.layer_id === l.layer_id
      );
      const pricing = pricingString(l, breakdown);
      if (!pricing) return [];
      return [
        multipleLayers
          ? `${esc(readableLayer(l))}: ${esc(pricing)}`
          : esc(pricing),
      ];
    })
    .join("<br>");

  const limitHtmlByPeril = limitEntries(
    report.limit_info,
    policy?.included_perils ?? []
  )
    .map((l) => `<strong>${esc(l.label)}:</strong> ${esc(l.value)}`)
    .join("<br>");

  const deductibleHtml = deductibleEntries(report.deductible_info)
    .map((d) => `<strong>${esc(d.label)}:</strong> ${esc(d.value)}`)
    .join("<br>");

  const riskDescription = buildRiskDescription(specialtyPropertyInfo);
  const coverages = coverageList(addresses);
  const itv = blendedItvPerSqft(addresses);

  const rows: { label: string; value: string | null }[] = [
    { label: "Account Name", value: esc(ci.company_name) },
    {
      label: "Effective Dates",
      value: effectiveDates ? esc(effectiveDates) : null,
    },
    {
      label: "Description of Risk",
      value: riskDescription ? esc(riskDescription) : null,
    },
    {
      label: "Peril(s) Insured",
      value: policy
        ? esc(
            perilSentenceFromEnabledPerils(
              policy.included_perils,
              policy.peril_sentence_override
            )
          )
        : null,
    },
    { label: "Coverage(s)", value: coverages ? esc(coverages) : null },
    {
      label: "Total Insurable Values",
      value: totalTiv > 0 ? esc(money(totalTiv)) : null,
    },
    {
      label: "Average ITV",
      value: itv != null ? esc(`$${Math.round(itv)}/sq ft`) : null,
    },
    { label: "Limit of Liability", value: limitHtml || null },
    { label: "Premium (Layer & Share) & Pricing", value: premiumHtml || null },
    {
      label: "Commission",
      value:
        policy?.broker_commission_percentage != null
          ? esc(`${policy.broker_commission_percentage}%`)
          : null,
    },
    { label: "Peril Limits", value: limitHtmlByPeril || null },
    { label: "Deductibles", value: deductibleHtml || null },
    { label: "Loss History", value: lossHistorySummary(report.loss_run) },
    {
      label: "Loss Run Comments",
      value: lossRunCommentsSummary(lossRunComments),
    },
    {
      label: "Form Type",
      value: policy?.form_selection ? esc(policy.form_selection) : null,
    },
  ].filter((row) => row.value != null && row.value !== "");

  const tableRows = rows
    .map(
      (row) =>
        `<tr><td style="${LABEL_STYLE}">${esc(row.label)}</td>` +
        `<td style="${VALUE_STYLE}">${row.value}</td></tr>`
    )
    .join("");

  return (
    `<table style="border-collapse:collapse;width:100%;max-width:760px;font-size:14px;">` +
    `<tbody>${tableRows}</tbody></table>`
  );
};
